import { useState } from 'react';

import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Manual digest trigger — lets Steve preview/send a digest off-cadence.
 *
 * Default mode = dry-run: re-renders, writes Markdown to S3, persists
 * provenance, but does NOT send via SES. Used for ad-hoc inspection of a
 * recent week's content (e.g., "what would last week's digest have looked
 * like with my updated routing rules?").
 *
 * Send mode requires explicit confirmation in the UI (checkbox).
 */
export function Digest(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.idToken;
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.triggerDigest>> | null>(null);

  const onTrigger = async (dryRun: boolean): Promise<void> => {
    if (idToken === null) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.triggerDigest(idToken, { dryRun });
      setResult(r);
      setConfirmSend(false);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'trigger failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <header className="mb-4">
        <h1 className="text-base font-mono text-ink">Digest</h1>
        <p className="text-2xs font-mono text-ink-muted uppercase tracking-wider mt-0.5">
          weekly · sunday 21:00 aest cron · manual trigger below
        </p>
      </header>

      <section className="card p-4 mb-4">
        <h2 className="text-sm font-mono mb-2">Manual trigger</h2>
        <p className="text-2xs font-mono text-ink-dim mb-4 leading-relaxed">
          Renders the most recent fully-finished week (Mon–Sun, UTC). Dry-run writes the
          Markdown to S3 and the <code className="text-accent">digests</code> table but does
          NOT send via SES. Send mode requires the explicit checkbox below.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => { void onTrigger(true); }}
            disabled={busy}
            className="text-sm font-mono uppercase tracking-wider px-4 py-2 rounded border border-bg-line hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy && !confirmSend ? '…' : 'Dry-run'}
          </button>

          <label className="flex items-center gap-2 text-2xs font-mono text-ink-dim">
            <input
              type="checkbox"
              checked={confirmSend}
              onChange={(e) => { setConfirmSend(e.target.checked); }}
              className="accent-accent"
            />
            <span>I want to send via SES</span>
          </label>

          <button
            onClick={() => { void onTrigger(false); }}
            disabled={busy || !confirmSend}
            className="text-sm font-mono uppercase tracking-wider px-4 py-2 rounded bg-accent hover:bg-accent-hover text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {busy && confirmSend ? '…' : 'Send digest now'}
          </button>
        </div>
      </section>

      {error !== null && (
        <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded mb-4">
          {error}
        </p>
      )}

      {result !== null && (
        <section className="card p-4">
          <h2 className="text-sm font-mono mb-3">
            Last trigger:{' '}
            <span className={result.dry_run ? 'pill-dim' : 'pill-ok'}>
              {result.dry_run ? 'dry-run' : 'sent'}
            </span>
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-2xs font-mono">
            <Row label="Week" value={`${result.result.week_start} → ${result.result.week_end}`} />
            <Row label="Items" value={String(result.result.item_count)} />
            <Row label="S3 key" value={result.result.s3_markdown_key} />
            {result.result.ses_message_id !== null && (
              <Row label="SES message ID" value={result.result.ses_message_id} />
            )}
            {result.result.truncated && (
              <Row label="Truncated" value="yes — content over word cap" />
            )}
            {result.result.warnings.length > 0 && (
              <Row label="Warnings" value={result.result.warnings.join('; ')} />
            )}
          </dl>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex gap-3 sm:contents">
      <dt className="text-ink-muted uppercase tracking-wider sm:text-right">{label}</dt>
      <dd className="text-ink break-all">{value}</dd>
    </div>
  );
}
